// Catalog.ID Client — Recovery Sheet Verdict (Addendum §6.4 / §14.2)
// ============================================================================
// The pure half of the verification page. A custodian holds one Recovery Sheet
// and nothing else — no other sheet, no bundle, no account of their own,
// possibly no living relationship with the member. They scan the QR and are
// entitled to exactly four answers:
//
//   1. Is the sheet intact?  — self-contained: every sheet carries the per-set
//      verification key and the MAC computed under it (§4.2), so `decodeRecoveryCard`
//      settles this in the browser with no network at all.
//   2. Which sheet is it?    — letter, position in the set, k-of-n, purpose, age.
//   3. Whose account is it?  — the node names the account; the sheet's own
//      account_fingerprint says whether the two agree.
//   4. Is it still current?  — the ONLY question the node answers, and it
//      answers it binary (§6.4: a re-issue supersedes every sheet of the set
//      it replaces, and a custodian should learn that at a quiet moment).
//
// Verification never reconstructs anything — a page that could reconstruct from
// one sheet would contradict the threshold (§6.4). Only the recovery-set id is
// ever transmitted; the share bytes, the verification key and the MAC stay on
// the device.
//
// Everything here is pure and DOM-free so the wording and the combination
// rules are testable on their own.

import { cardChecksum, type RecoveryCardPayload } from '../crypto/recovery-card';
import { accountFingerprint } from '../crypto/bundle';
import { cardTypeLetter } from '../components/booklet/shared';

/** The node's answer to POST /api/recoverykit/verify. Binary by design. */
export type VerifyAnswer =
    | {
        status: 'current';
        cid: string;
        accountType: string;
        thresholdK: number;
        totalSharesN: number;
        /** ISO 8601 — when the kit record was created on the node. */
        createdAt: string;
    }
    | { status: 'not_current' };

/** What the sheet says about itself. Null exactly when integrity failed —
 *  a payload that did not decode describes nothing. */
export interface SheetFacts {
    /** Sheet type as printed: share index 1 → "A", 2 → "B", … */
    letter: string;
    shareIndex: number;
    totalSharesN: number;
    thresholdK: number;
    /** The purpose the member assigned to this sheet type (`card_label`). */
    purpose: string;
    createdAt: Date;
}

/** The account the NODE names, and whether the sheet agrees. Null whenever the
 *  node named no account (not current, or unreachable) — an unnamed account
 *  has no binding to assess. */
export interface AccountFacts {
    cid: string;
    accountType: string;
    /** True when accountFingerprint(cid) equals the sheet's account_fingerprint. */
    bound: boolean;
}

export type SheetCurrency = 'current' | 'not_current' | 'unchecked';

export interface SheetVerdict {
    integrity: 'ok' | 'failed';
    /** The sheet fingerprint printed under the QR, computed from the bytes that
     *  were actually SCANNED — never from a re-encode of the decoded payload,
     *  which would launder away exactly the damage this is meant to expose.
     *  Empty when the scan produced too few bytes to hold a MAC. */
    fingerprint: string;
    sheet: SheetFacts | null;
    account: AccountFacts | null;
    currency: SheetCurrency;
    /** Short, plain-language sentences to show the custodian, in order. */
    lines: string[];
}

export interface AssessSheetInput {
    /** The raw QR payload bytes as scanned. */
    rawBytes: Uint8Array;
    /** The decoded payload, or null when `decodeRecoveryCard` rejected the bytes. */
    payload: RecoveryCardPayload | null;
    /** The node's answer, or null when the node could not be reached. */
    server: VerifyAnswer | null;
}

const MAC_LENGTH = 32;

export const INTEGRITY_OK_LINE =
    'The sheet scanned cleanly and its integrity check passed — it is an unaltered share of the recovery set it names.';

export const INTEGRITY_FAILED_LINE =
    'This code could not be read as a Catalog.ID Recovery Sheet. The sheet may be damaged, '
    + 'misprinted, or not a Recovery Sheet at all — try a sharper, straight-on scan before concluding anything.';

export const NOT_CURRENT_LINE =
    'This node has no current recovery kit matching this sheet. It may have been '
    + 'replaced by a newer kit, or it belongs to an account homed on another node — '
    + 'ask the account holder for a sheet from the current kit.';

export const UNCHECKED_LINE =
    'Could not reach the node to check whether this sheet is current.';

export function currentLine(cid: string): string {
    return `This sheet belongs to the current recovery kit of account ${cid}.`;
}

export function unboundLine(cid: string): string {
    return `Warning: this sheet does not belong to the account this node names (${cid}). `
        + 'The sheet is internally sound, but it is not a share of that account\'s kit — '
        + 'check it with the account holder before relying on it.';
}

/**
 * Turn a scan plus the node's answer into the verdict the page renders.
 *
 * Async only because the account-binding check hashes the CID
 * (`accountFingerprint`, WebCrypto).
 */
export async function assessSheet(input: AssessSheetInput): Promise<SheetVerdict> {
    const { rawBytes, payload, server } = input;

    const fingerprint = rawBytes.length >= MAC_LENGTH ? cardChecksum(rawBytes) : '';

    // A payload that did not decode supports no further claim. In particular it
    // carries no recovery-set id, so nothing was — or could have been — asked of
    // the node; any answer handed in alongside it belongs to a different scan and
    // is deliberately ignored rather than attributed to this sheet.
    if (!payload) {
        return {
            integrity: 'failed',
            fingerprint,
            sheet: null,
            account: null,
            currency: 'unchecked',
            lines: [INTEGRITY_FAILED_LINE],
        };
    }

    const sheet: SheetFacts = {
        letter: cardTypeLetter(payload.shareIndex),
        shareIndex: payload.shareIndex,
        totalSharesN: payload.totalSharesN,
        thresholdK: payload.thresholdK,
        purpose: payload.cardLabel,
        createdAt: new Date(payload.createdAt * 1000),
    };

    const currency: SheetCurrency = server === null ? 'unchecked' : server.status;

    // The node names an account only when it recognised the set as current; a
    // "not current" answer says nothing about whose sheet this is, on purpose.
    let account: AccountFacts | null = null;
    if (server !== null && server.status === 'current') {
        account = {
            cid: server.cid,
            accountType: server.accountType,
            bound: await accountFingerprint(server.cid) === payload.accountFingerprint,
        };
    }

    const lines: string[] = [INTEGRITY_OK_LINE];
    if (currency === 'current' && account) lines.push(currentLine(account.cid));
    else if (currency === 'not_current') lines.push(NOT_CURRENT_LINE);
    else lines.push(UNCHECKED_LINE);
    if (account && !account.bound) lines.push(unboundLine(account.cid));

    return { integrity: 'ok', fingerprint, sheet, account, currency, lines };
}
